import { useTranslation } from "react-i18next"
import { Label, Pie, PieChart } from "recharts"

import type { ChartConfig } from "@/components/ui/chart"
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart"
import type { DashboardPaymentTypeEnum } from "@/enums"
import { useDashboardPayment } from "@/hooks"

import { DashboardChartSkeleton } from "./skeleton"
type DashboardApprovalChartProps = {
  type: DashboardPaymentTypeEnum
  searchParams?: SearchParamsType
  enabled?: boolean
}
export const DashboardApprovalChart: React.FC<
  DashboardApprovalChartProps
> = ({
  type,
  searchParams,
  enabled = true,
}: DashboardApprovalChartProps) => {
  const { t } = useTranslation()
  const { data, isLoading } = useDashboardPayment({
    dashboardType: type,
    ...searchParams,
    enabled,
  })
  if (isLoading) {
    return <DashboardChartSkeleton />
  }
  if (
    !data ||
    !data.indicator ||
    data.indicator.length === 0
  ) {
    return (
      <div className="flex min-h-[200px] items-center justify-center text-sm text-muted-foreground">
        {t("pages.dashboard.noDataAvailable")}
      </div>
    )
  }
  const chartData = data?.indicator.map((item) => ({
    key: item.key,
    value: +item.value.replace("%", ""),
    fill:
      item.key === "Aprovado"
        ? "hsl(var(--chart-approved))"
        : "hsl(var(--secondary))",
  }))
  const totalApproved = chartData?.find(
    (item) => item.key === "Aprovado"
  )?.value
  const chartConfig = {
    aprovado: {
      label: "Aprovado",
    },
    Rejeitado: {
      label: "Rejeitado",
    },
  } as ChartConfig
  return (
    <ChartContainer
      config={chartConfig}
      className="mx-auto aspect-square max-h-[250px]"
    >
      <PieChart>
        <ChartTooltip
          cursor={false}
          content={<ChartTooltipContent hideLabel />}
        />
        <Pie
          data={chartData}
          dataKey="value"
          nameKey="key"
          innerRadius={70}
        >
          <Label
            content={({ viewBox }) => {
              if (
                viewBox &&
                "cx" in viewBox &&
                "cy" in viewBox
              ) {
                return (
                  <text
                    x={viewBox.cx}
                    y={viewBox.cy}
                    textAnchor="middle"
                    dominantBaseline="middle"
                  >
                    <tspan
                      x={viewBox.cx}
                      y={viewBox.cy}
                      className="fill-foreground p-4 text-5xl font-bold"
                    >
                      {totalApproved?.toLocaleString()}{" "}
                      <tspan className="m-2 text-xl">
                        %
                      </tspan>
                    </tspan>
                    <tspan
                      x={viewBox.cx}
                      y={(viewBox.cy || 0) + 35}
                      className="fill-muted-foreground text-[16px] font-bold"
                    >
                      Aprovados
                    </tspan>
                  </text>
                )
              }
            }}
          />
        </Pie>
      </PieChart>
    </ChartContainer>
  )
}
